import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'

interface StampAdjustParams {
  memberId: string
  stampsDelta: number
  correctionReason: string
  correctionNote: string
}

interface RewardAdjustParams {
  memberId: string
  rewardItemId: string
  rewardsDelta: number
  correctionReason: string
  correctionNote: string
}

interface RpcResult {
  success: boolean
  error?: string
  newStampTotal?: number
  rewardsUnlocked?: number
  newCount?: number
}

export function useAdminAdjustStamps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: StampAdjustParams) => {
      const { data, error } = await supabase.rpc('admin_adjust_member_stamps', {
        p_member_id: params.memberId,
        p_stamps_delta: params.stampsDelta,
        p_correction_reason: params.correctionReason,
        p_correction_note: params.correctionNote,
      })

      if (error) {
        reportUnexpectedError(error)
        throw error
      }

      const result = data as unknown as RpcResult
      if (!result.success) {
        const failure = new Error(result.error ?? 'unknown_error')
        reportUnexpectedError(failure)
        throw failure
      }

      return result
    },
    onSuccess: (_data, params) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-member-detail', params.memberId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-member-search'] })
      void queryClient.invalidateQueries({ queryKey: ['recent-activity'] })
    },
  })
}

export function useAdminAdjustRewards() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: RewardAdjustParams) => {
      const { data, error } = await supabase.rpc('admin_adjust_member_rewards', {
        p_member_id: params.memberId,
        p_reward_item_id: params.rewardItemId,
        p_rewards_delta: params.rewardsDelta,
        p_correction_reason: params.correctionReason,
        p_correction_note: params.correctionNote,
      })

      if (error) {
        reportUnexpectedError(error)
        throw error
      }

      const result = data as unknown as RpcResult
      if (!result.success) {
        const failure = new Error(result.error ?? 'unknown_error')
        reportUnexpectedError(failure)
        throw failure
      }

      return result
    },
    onSuccess: (_data, params) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-member-detail', params.memberId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-member-search'] })
      void queryClient.invalidateQueries({ queryKey: ['recent-activity'] })
    },
  })
}